/*
[GNU Affero General Public License (AGPL)](https://www.gnu.org/licenses/agpl-3.0.html)

VeloceSupabase - A library for working with supabase made in Angular v17+ made with care by Colibryx Srl in Italy.
Copyright (C) 2025 Colibryx S.r.l.

This program is free software: you can redistribute it and/or modify
it under the terms of the GNU Affero General Public License as published by
the Free Software Foundation, either version 3 of the License, or
(at your option) any later version.

This program is distributed in the hope that it will be useful,
but WITHOUT ANY WARRANTY; without even the implied warranty of
MERCHANTABILITY or FITNESS FOR A PARTICULAR PURPOSE. See the
GNU Affero General Public License for more details.

You should have received a copy of the GNU Affero General Public License
along with this program. If not, see <https://www.gnu.org/licenses/>.
*/

import {
	ChangeDetectorRef,
	ViewContainerRef,
	computed,
	effect,
	inject,
	signal,
} from "@angular/core";
import { toObservable } from "@angular/core/rxjs-interop";
import { ActivatedRoute, NavigationEnd, Router } from "@angular/router";
import { HiddenFieldsService } from "@app/@services/data/hidden-fields.service";
import { ActionLoggerService } from "@app/@services/generic/action-logger.service";
import { RealtimeCursorsService } from "@app/@services/generic/realtime-cursors.service";
import { RealtimeOnlinePresenceService } from "@app/@services/generic/realtime-online-presence.service";
import { ThemeService } from "@app/@services/generic/theme.service";
import { UsersRepositoryService } from "@app/@services/repository/users-repository.service";
import { AuthService } from "@app/@services/shared/auth.service";
import { AuthStateService } from "@app/@services/shared/auth-state.service";
import { LogoutService } from "@app/@services/shared/logout.service";
import { ConfirmationService } from "@app/widgets/base/confirmation/confirmation-manager.service";
import { DialogService } from "@app/widgets/base/dialog/dialog.service";
import { ToastService } from "@app/widgets/base/notification/toast.service";
import { CbRoutesUtils } from "../../routes/cb-routes-utils";

/**
 * Abstract class representing a CbAppImplementation.
 */
export abstract class AppView {
	protected readonly authService: AuthService = inject(AuthService);
	protected readonly authStateService: AuthStateService =
		inject(AuthStateService);
	protected readonly logoutService: LogoutService = inject(LogoutService);
	protected readonly router: Router = inject(Router);
	protected readonly activatedRoute: ActivatedRoute = inject(ActivatedRoute);
	protected readonly toastService: ToastService = inject(ToastService);
	protected readonly dialogService: DialogService = inject(DialogService);
	protected readonly confirmationService: ConfirmationService =
		inject(ConfirmationService);
	private readonly actionLoggerService: ActionLoggerService =
		inject(ActionLoggerService);
	protected cdr: ChangeDetectorRef = inject(ChangeDetectorRef);
	protected readonly realtimePresenceService: RealtimeOnlinePresenceService =
		inject(RealtimeOnlinePresenceService);
	protected readonly realtimeCursorsService: RealtimeCursorsService = inject(
		RealtimeCursorsService,
	);
	protected readonly usersRepositoryService: UsersRepositoryService = inject(
		UsersRepositoryService,
	);
	/* protected readonly realtimeMobileFeatureService: RealtimeMobileFeatureService =
		inject(RealtimeMobileFeatureService); */
	private readonly themeService: ThemeService = inject(ThemeService);
	private readonly hiddenFieldsService: HiddenFieldsService =
		inject(HiddenFieldsService);

	public darkModeEnabled = signal(false);

	// ViewContainerRef for the services
	private readonly vcr: ViewContainerRef = inject(ViewContainerRef);

	// Whether the sidebar should be shown
	public showSidebar = signal(false);

	public authName = signal<string | null>(null);
	public authEmail = signal<string | null>(null);

	public readonly isAuthenticated = computed(() => this.authService.isLogged());

	constructor() {
		// Check if the sidebar should be shown every time the route changes
		this.router.events.subscribe((event) => {
			if (event instanceof NavigationEnd) {
				this.showSidebar.set(
					CbRoutesUtils.showSidebar(event.urlAfterRedirects) || false,
				);

				this.actionLoggerService.registerAction(
					`Navigated to "${event.urlAfterRedirects}"`,
					event,
				);
			}
		});

		this.themeService.theme$.subscribe((theme) => {
			this.darkModeEnabled.set(theme === "dark");
		});

		// Subscribe to user info changes
		effect(() => {
			const userInfo = this.authStateService.currentUserInfo();
			this.authName.set(userInfo?.name || null);
			this.authEmail.set(
				this.authStateService.currentAuthSession()?.user.email || null,
			);
		});

		// Initialize the ViewContainerRefs for the services
		this.initViewContainerRefs();
	}

	/**
	 * Initializes the ViewContainerRefs for the services.
	 */
	private initViewContainerRefs(): void {
		this.toastService.setViewContainerRef(this.vcr);
		this.dialogService.setViewContainerRef(this.vcr);
		this.confirmationService.setViewContainerRef(this.vcr);
		this.realtimeCursorsService.setViewContainerRef(this.vcr);
	}

	/**
	 * Logs out the user.
	 */
	public logout() {
		this.logoutService.logout();
	}
}
